import * as Tooltip from '@radix-ui/react-tooltip'
import { ChevronDown, ChevronRight, Scale } from 'lucide-react'
import { useRef, useState } from 'react'

import { ProficiencyRangeSlider } from '@/components/ui/proficiency-range-slider'
import { getPresetProficiencyRange } from '@/lib/recommendation/company-proficiency-presets'
import {
  classifyMasteryBucket,
  formatRollupWeight,
  hasCustomRollupWeight,
} from '@/lib/recommendation/proficiency-range'
import { cn } from '@/lib/utils'
import { toastError } from '@/lib/utils/toast-helpers'
import {
  countCustomWeightedSubtopics,
  rollUpTopicMastery,
} from './_lib/derivations'
import { DiffMarker } from './diff-marker'
import { RecalculatingBanner } from './recalculating-banner'
import {
  type ProficiencyThresholdUpdate,
  useRecommendationEngineMock,
} from './recommendation-engine-context'
import type { SubtopicState, TopicState } from './types'

/** The drafts map minus one entry — what a landed save retires. */
function withoutKey<V>(
  drafts: Record<string, V>,
  key: string,
): Record<string, V> {
  const cleared = { ...drafts }
  delete cleared[key]
  return cleared
}

function proficiencyBucketLabel(
  current: number,
  min: number,
  max: number,
): string {
  return classifyMasteryBucket(current, min, max)
}

function subtopicMinThreshold(
  subtopic: SubtopicState,
  topic: TopicState,
  draftSubtopicMinThresholds: Record<string, number>,
): number {
  return (
    draftSubtopicMinThresholds[subtopic.id] ??
    subtopic.minProficiencyThreshold ??
    topic.minProficiencyThreshold
  )
}

function subtopicMaxThreshold(
  subtopic: SubtopicState,
  topic: TopicState,
  draftSubtopicMaxThresholds: Record<string, number>,
): number {
  return (
    draftSubtopicMaxThresholds[subtopic.id] ??
    subtopic.maxProficiencyThreshold ??
    topic.maxProficiencyThreshold
  )
}

function resolvedRollupWeight(
  subtopic: SubtopicState,
  draftWeight?: string,
): number {
  if (draftWeight !== undefined) {
    const parsed = Number(draftWeight)
    if (Number.isFinite(parsed) && parsed > 0) return parsed
  }
  return subtopic.rollupWeight ?? 1
}

function RollupWeightIcon({
  weight,
  labelName,
}: {
  weight: number
  labelName: string
}) {
  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <span
          data-testid="rollup-weight-icon"
          className="text-amber-600 dark:text-amber-400 inline-flex shrink-0"
        >
          <Scale size={12} aria-hidden="true" />
        </span>
      </Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content
          side="top"
          className="bg-background text-foreground border-border z-50 rounded-md border px-2 py-1 text-xs shadow-md"
        >
          Rollup weight: {formatRollupWeight(weight)} (default 1)
          <Tooltip.Arrow className="fill-background" />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  )
}

/**
 * Proficiency Goals tab — target company preset and per-topic / per-subtopic
 * range thresholds + rollup weighting controls.
 */
export function ProficiencyGoalsView() {
  const {
    topics,
    setTopicMinThreshold,
    setTopicMaxThreshold,
    setSubtopicMinThreshold,
    setSubtopicMaxThreshold,
    setSubtopicRollupWeight,
    applyCompanyPreset,
    companyProficiencyPresets,
    updateProficiencyThresholds,
    engineEnabled,
    pendingDiff,
  } = useRecommendationEngineMock()

  const [companyId, setCompanyId] = useState<string>('')
  const [applyingPreset, setApplyingPreset] = useState(false)
  const [showWeights, setShowWeights] = useState(false)
  const [topicWeightVisibility, setTopicWeightVisibility] = useState<
    Record<string, boolean>
  >({})
  const [draftTopicMinThresholds, setDraftTopicMinThresholds] = useState<
    Record<string, number>
  >({})
  const [draftTopicMaxThresholds, setDraftTopicMaxThresholds] = useState<
    Record<string, number>
  >({})
  const [draftSubtopicMinThresholds, setDraftSubtopicMinThresholds] = useState<
    Record<string, number>
  >({})
  const [draftSubtopicMaxThresholds, setDraftSubtopicMaxThresholds] = useState<
    Record<string, number>
  >({})
  const [draftSubtopicWeights, setDraftSubtopicWeights] = useState<
    Record<string, string>
  >({})
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})

  const applyPreset = async () => {
    if (!companyId) return
    const preset = companyProficiencyPresets.find(
      (item) => item.id === companyId,
    )
    if (!preset) return
    const { minProficiencyThreshold, maxProficiencyThreshold } =
      getPresetProficiencyRange(preset.id)
    if (!updateProficiencyThresholds) {
      applyCompanyPreset(companyId)
      setDraftTopicMinThresholds({})
      setDraftTopicMaxThresholds({})
      setDraftSubtopicMinThresholds({})
      setDraftSubtopicMaxThresholds({})
      return
    }
    try {
      setApplyingPreset(true)
      await updateProficiencyThresholds({
        topicUpdates: topics.map((topic) => ({
          id: topic.id,
          minProficiencyThreshold,
          maxProficiencyThreshold,
        })),
        subtopicUpdates: topics.flatMap((topic) =>
          topic.subtopics.map((subtopic) => ({
            id: subtopic.id,
            minProficiencyThreshold,
            maxProficiencyThreshold,
          })),
        ),
      })
      setDraftTopicMinThresholds({})
      setDraftTopicMaxThresholds({})
      setDraftSubtopicMinThresholds({})
      setDraftSubtopicMaxThresholds({})
    } catch (error) {
      toastError(error, 'Failed to apply preset')
    } finally {
      setApplyingPreset(false)
    }
  }

  /**
   * Per draft key, the id of the latest commit. Saves overlap — pointer-up,
   * blur and Enter all commit, and each save awaits a refetch — so a save
   * retires its draft only if no later commit on that key has started since;
   * a draft that moved on belongs to the save behind it, even when it moved
   * back to the same value.
   */
  const latestCommitByKey = useRef<Record<string, number>>({})
  const commitCounter = useRef(0)

  /**
   * Persist one field edit and retire its draft only once the save has
   * landed and it is still the latest commit on that draft. Clearing the
   * draft up front let a failed save snap the control back to the stale
   * server value with nothing to say why (LG-421); a failure now keeps the
   * draft where the coach left it and says so.
   */
  const commitThresholdUpdate = async (
    draftKey: string,
    payload: ProficiencyThresholdUpdate,
    applyLocally: () => void,
    clearDraft: () => void,
  ) => {
    if (!updateProficiencyThresholds) {
      applyLocally()
      clearDraft()
      return
    }
    commitCounter.current += 1
    const commitId = commitCounter.current
    latestCommitByKey.current[draftKey] = commitId
    try {
      await updateProficiencyThresholds(payload)
      if (latestCommitByKey.current[draftKey] === commitId) clearDraft()
    } catch (error) {
      toastError(error, 'Failed to save proficiency goal')
    }
  }

  const toggleExpanded = (topicId: string) => {
    setExpanded((prev) => ({ ...prev, [topicId]: !prev[topicId] }))
  }
  const toggleTopicWeightVisibility = (topicId: string) => {
    setTopicWeightVisibility((prev) => ({ ...prev, [topicId]: !prev[topicId] }))
  }

  return (
    <Tooltip.Provider delayDuration={200}>
      <div className="space-y-6">
        <RecalculatingBanner />

        <div className="border-border rounded-lg border p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[200px] flex-1">
              <label
                htmlFor="re-target-company"
                className="text-foreground mb-1 block text-xs font-medium"
              >
                Target Company{' '}
                <span className="text-muted-foreground font-normal">
                  (optional)
                </span>
              </label>
              <select
                id="re-target-company"
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
                disabled={!engineEnabled}
                className="bg-muted border-border text-foreground w-full rounded-lg border px-3 py-2 text-sm"
              >
                <option value="">No target company</option>
                {companyProficiencyPresets.map((preset) => (
                  <option key={preset.id} value={preset.id}>
                    {preset.name}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={() => void applyPreset()}
              disabled={!companyId || !engineEnabled || applyingPreset}
              className="bg-primary text-primary-foreground rounded-lg px-3 py-2 text-sm font-medium transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {applyingPreset ? 'Applying…' : 'Apply preset'}
            </button>
          </div>
          <p className="text-muted-foreground mt-2 text-xs">
            Applying a preset fills each topic/subtopic min-max proficiency
            range. Expand a topic to fine-tune individual subtopics or exclude
            them from rollups and recommendations.
          </p>
        </div>

        <div>
          <div className="mb-1 flex items-center justify-between gap-3">
            <h3 className="text-foreground text-sm font-semibold">
              Proficiency goals
            </h3>
            <button
              type="button"
              onClick={() => setShowWeights((prev) => !prev)}
              className="text-xs font-medium text-sky-700 hover:underline dark:text-sky-300"
            >
              {showWeights ? 'Hide weights' : 'Show weights'}
            </button>
          </div>
          <p className="text-muted-foreground mb-3 text-xs">
            Topic ranges set default min/max goals for subtopics; subtopic
            ranges override that default. Disabled subtopics are excluded from
            mastery rollups and recommendation processing (stored FSRS state is
            kept).
          </p>
          <div className="divide-border border-border divide-y rounded-lg border">
            {topics.map((topic, index) => (
              <TopicProficiencyRow
                key={topic.id}
                topic={topic}
                index={index}
                expanded={expanded[topic.id] ?? false}
                onToggleExpand={() => toggleExpanded(topic.id)}
                showWeights={showWeights}
                showTopicWeights={topicWeightVisibility[topic.id] ?? false}
                onToggleTopicWeights={() =>
                  toggleTopicWeightVisibility(topic.id)
                }
                topicMinThreshold={
                  draftTopicMinThresholds[topic.id] ??
                  topic.minProficiencyThreshold
                }
                topicMaxThreshold={
                  draftTopicMaxThresholds[topic.id] ??
                  topic.maxProficiencyThreshold
                }
                draftSubtopicMinThresholds={draftSubtopicMinThresholds}
                draftSubtopicMaxThresholds={draftSubtopicMaxThresholds}
                draftSubtopicWeights={draftSubtopicWeights}
                pendingDiff={pendingDiff}
                engineEnabled={engineEnabled}
                onTopicMinDraftChange={(next) =>
                  setDraftTopicMinThresholds((prev) => ({
                    ...prev,
                    [topic.id]: next,
                  }))
                }
                onTopicMaxDraftChange={(next) =>
                  setDraftTopicMaxThresholds((prev) => ({
                    ...prev,
                    [topic.id]: next,
                  }))
                }
                onTopicMinCommit={(next) =>
                  void commitThresholdUpdate(
                    `topic-min:${topic.id}`,
                    {
                      topicUpdates: [
                        { id: topic.id, minProficiencyThreshold: next },
                      ],
                    },
                    () => setTopicMinThreshold(topic.id, next),
                    () =>
                      setDraftTopicMinThresholds((prev) =>
                        withoutKey(prev, topic.id),
                      ),
                  )
                }
                onTopicMaxCommit={(next) =>
                  void commitThresholdUpdate(
                    `topic-max:${topic.id}`,
                    {
                      topicUpdates: [
                        { id: topic.id, maxProficiencyThreshold: next },
                      ],
                    },
                    () => setTopicMaxThreshold(topic.id, next),
                    () =>
                      setDraftTopicMaxThresholds((prev) =>
                        withoutKey(prev, topic.id),
                      ),
                  )
                }
                onSubtopicMinDraftChange={(subtopicId, next) =>
                  setDraftSubtopicMinThresholds((prev) => ({
                    ...prev,
                    [subtopicId]: next,
                  }))
                }
                onSubtopicMaxDraftChange={(subtopicId, next) =>
                  setDraftSubtopicMaxThresholds((prev) => ({
                    ...prev,
                    [subtopicId]: next,
                  }))
                }
                onSubtopicMinCommit={(subtopicId, next) =>
                  void commitThresholdUpdate(
                    `subtopic-min:${subtopicId}`,
                    {
                      subtopicUpdates: [
                        { id: subtopicId, minProficiencyThreshold: next },
                      ],
                    },
                    () => setSubtopicMinThreshold(topic.id, subtopicId, next),
                    () =>
                      setDraftSubtopicMinThresholds((prev) =>
                        withoutKey(prev, subtopicId),
                      ),
                  )
                }
                onSubtopicMaxCommit={(subtopicId, next) =>
                  void commitThresholdUpdate(
                    `subtopic-max:${subtopicId}`,
                    {
                      subtopicUpdates: [
                        { id: subtopicId, maxProficiencyThreshold: next },
                      ],
                    },
                    () => setSubtopicMaxThreshold(topic.id, subtopicId, next),
                    () =>
                      setDraftSubtopicMaxThresholds((prev) =>
                        withoutKey(prev, subtopicId),
                      ),
                  )
                }
                onSubtopicWeightDraftChange={(subtopicId, next) =>
                  setDraftSubtopicWeights((prev) => ({
                    ...prev,
                    [subtopicId]: next,
                  }))
                }
                onSubtopicWeightCommit={(subtopicId, next) =>
                  void commitThresholdUpdate(
                    `subtopic-weight:${subtopicId}`,
                    {
                      subtopicUpdates: [{ id: subtopicId, rollupWeight: next }],
                    },
                    () => setSubtopicRollupWeight(topic.id, subtopicId, next),
                    () =>
                      setDraftSubtopicWeights((prev) =>
                        withoutKey(prev, subtopicId),
                      ),
                  )
                }
              />
            ))}
          </div>
        </div>
      </div>
    </Tooltip.Provider>
  )
}

function TopicProficiencyRow({
  topic,
  index,
  expanded,
  onToggleExpand,
  showWeights,
  showTopicWeights,
  onToggleTopicWeights,
  topicMinThreshold,
  topicMaxThreshold,
  draftSubtopicMinThresholds,
  draftSubtopicMaxThresholds,
  draftSubtopicWeights,
  pendingDiff,
  engineEnabled,
  onTopicMinDraftChange,
  onTopicMaxDraftChange,
  onTopicMinCommit,
  onTopicMaxCommit,
  onSubtopicMinDraftChange,
  onSubtopicMaxDraftChange,
  onSubtopicMinCommit,
  onSubtopicMaxCommit,
  onSubtopicWeightDraftChange,
  onSubtopicWeightCommit,
}: {
  topic: TopicState
  index: number
  expanded: boolean
  onToggleExpand: () => void
  showWeights: boolean
  showTopicWeights: boolean
  onToggleTopicWeights: () => void
  topicMinThreshold: number
  topicMaxThreshold: number
  draftSubtopicMinThresholds: Record<string, number>
  draftSubtopicMaxThresholds: Record<string, number>
  draftSubtopicWeights: Record<string, string>
  pendingDiff: ReturnType<typeof useRecommendationEngineMock>['pendingDiff']
  engineEnabled: boolean
  onTopicMinDraftChange: (value: number) => void
  onTopicMaxDraftChange: (value: number) => void
  onTopicMinCommit: (value: number) => void
  onTopicMaxCommit: (value: number) => void
  onSubtopicMinDraftChange: (subtopicId: string, value: number) => void
  onSubtopicMaxDraftChange: (subtopicId: string, value: number) => void
  onSubtopicMinCommit: (subtopicId: string, value: number) => void
  onSubtopicMaxCommit: (subtopicId: string, value: number) => void
  onSubtopicWeightDraftChange: (subtopicId: string, value: string) => void
  onSubtopicWeightCommit: (subtopicId: string, value: number) => void
}) {
  const currentMastery = rollUpTopicMastery(topic.subtopics, topic)
  const bucket = proficiencyBucketLabel(
    currentMastery,
    topicMinThreshold,
    topicMaxThreshold,
  )
  const minThresholdDiff = pendingDiff.find(
    (item) => item.key === `topic-min-threshold:${topic.id}`,
  )
  const maxThresholdDiff = pendingDiff.find(
    (item) => item.key === `topic-max-threshold:${topic.id}`,
  )
  const controlsDisabled = !topic.includedInPlan || !engineEnabled
  const shouldShowWeights = showWeights || showTopicWeights
  const customWeightedCount = countCustomWeightedSubtopics(
    topic.subtopics,
    topic,
  )

  return (
    <div
      className={cn(!topic.includedInPlan && 'bg-muted/40 opacity-60')}
      data-topic-id={topic.id}
    >
      <div className="flex items-center gap-4 p-3">
        <PlanToggleButton topicId={topic.id} labelName={topic.name} />
        <button
          type="button"
          onClick={onToggleExpand}
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${topic.name} subtopics`}
          className="text-muted-foreground hover:text-foreground shrink-0 p-0.5"
        >
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        <span className="text-muted-foreground w-5 shrink-0 text-right text-xs">
          {index + 1}
        </span>
        <div className="w-44 shrink-0">
          <div className="flex items-center gap-1.5">
            <p className="text-foreground min-w-0 truncate text-sm font-medium">
              {topic.name}
            </p>
            {!expanded && customWeightedCount > 0 && (
              <span
                data-testid="topic-weighted-pill"
                className="border-amber-200 bg-amber-100 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/20 dark:text-amber-300 shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] font-medium tabular-nums"
              >
                {customWeightedCount} weighted
              </span>
            )}
          </div>
          <p className="text-muted-foreground text-xs">
            {topic.chapter ? `Ch. ${topic.chapter} · ` : ''}
            {bucket} · weighted rollup EM {currentMastery}
            {customWeightedCount > 0
              ? ` · ${customWeightedCount} custom weight${customWeightedCount === 1 ? '' : 's'}`
              : ''}
            {expanded ? ` · ${topic.subtopics.length} subtopics` : ''}
          </p>
          {minThresholdDiff && (
            <div
              data-testid="topic-min-diff-marker"
              data-diff-key={minThresholdDiff.key}
              className="mt-1"
            >
              <DiffMarker
                previousValue={minThresholdDiff.previousValue}
                currentValue={minThresholdDiff.currentValue}
              />
            </div>
          )}
          {maxThresholdDiff && (
            <div
              data-testid="topic-max-diff-marker"
              data-diff-key={maxThresholdDiff.key}
              className="mt-1"
            >
              <DiffMarker
                previousValue={maxThresholdDiff.previousValue}
                currentValue={maxThresholdDiff.currentValue}
              />
            </div>
          )}
        </div>
        <div className="flex-1">
          <div className="relative pb-5">
            <ProficiencyRangeSlider
              min={topicMinThreshold}
              max={topicMaxThreshold}
              currentMastery={currentMastery}
              onMinChange={onTopicMinDraftChange}
              onMaxChange={onTopicMaxDraftChange}
              onMinCommit={onTopicMinCommit}
              onMaxCommit={onTopicMaxCommit}
              hideCurrentMarker={controlsDisabled}
              disabled={controlsDisabled}
              aria-label={`${topic.name} proficiency goal`}
            />
            <RangeValueLabels min={topicMinThreshold} max={topicMaxThreshold} />
          </div>
        </div>
      </div>
      {expanded && (
        <div className="border-border bg-muted/20 space-y-1 border-t px-3 py-2 pl-14">
          <div className="mb-1 flex items-center justify-end">
            <button
              type="button"
              onClick={onToggleTopicWeights}
              className="text-xs font-medium text-sky-700 hover:underline dark:text-sky-300"
            >
              {showTopicWeights
                ? 'Hide weights for this topic'
                : 'Show weights for this topic'}
            </button>
          </div>
          {topic.subtopics.map((subtopic) => (
            <SubtopicProficiencyRow
              key={subtopic.id}
              topic={topic}
              subtopic={subtopic}
              minThreshold={subtopicMinThreshold(
                subtopic,
                topic,
                draftSubtopicMinThresholds,
              )}
              maxThreshold={subtopicMaxThreshold(
                subtopic,
                topic,
                draftSubtopicMaxThresholds,
              )}
              shouldShowWeightInput={shouldShowWeights}
              draftWeight={draftSubtopicWeights[subtopic.id]}
              pendingDiff={pendingDiff}
              engineEnabled={engineEnabled}
              onMinDraftChange={(next) =>
                onSubtopicMinDraftChange(subtopic.id, next)
              }
              onMaxDraftChange={(next) =>
                onSubtopicMaxDraftChange(subtopic.id, next)
              }
              onMinCommit={(next) => onSubtopicMinCommit(subtopic.id, next)}
              onMaxCommit={(next) => onSubtopicMaxCommit(subtopic.id, next)}
              onWeightDraftChange={(next) =>
                onSubtopicWeightDraftChange(subtopic.id, next)
              }
              onWeightCommit={(next) =>
                onSubtopicWeightCommit(subtopic.id, next)
              }
            />
          ))}
        </div>
      )}
    </div>
  )
}

function RangeValueLabels({ min, max }: { min: number; max: number }) {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-x-0 bottom-0 h-4 text-[10px] text-muted-foreground"
    >
      <span
        className="absolute -translate-x-1/2 font-medium tabular-nums"
        style={{ left: `${min}%` }}
      >
        {min}
      </span>
      <span
        className="absolute -translate-x-1/2 font-medium tabular-nums"
        style={{ left: `${max}%` }}
      >
        {max}
      </span>
    </div>
  )
}

function SubtopicProficiencyRow({
  topic,
  subtopic,
  minThreshold,
  maxThreshold,
  shouldShowWeightInput,
  draftWeight,
  pendingDiff,
  engineEnabled,
  onMinDraftChange,
  onMaxDraftChange,
  onMinCommit,
  onMaxCommit,
  onWeightDraftChange,
  onWeightCommit,
}: {
  topic: TopicState
  subtopic: SubtopicState
  minThreshold: number
  maxThreshold: number
  shouldShowWeightInput: boolean
  draftWeight?: string
  pendingDiff: ReturnType<typeof useRecommendationEngineMock>['pendingDiff']
  engineEnabled: boolean
  onMinDraftChange: (value: number) => void
  onMaxDraftChange: (value: number) => void
  onMinCommit: (value: number) => void
  onMaxCommit: (value: number) => void
  onWeightDraftChange: (value: string) => void
  onWeightCommit: (value: number) => void
}) {
  const subtopicEnabled = topic.includedInPlan && subtopic.isEnabled !== false
  const controlsDisabled = !topic.includedInPlan || !engineEnabled
  const rangeDisabled = controlsDisabled || !subtopicEnabled
  const minThresholdDiff = pendingDiff.find(
    (item) => item.key === `subtopic-min-threshold:${subtopic.id}`,
  )
  const maxThresholdDiff = pendingDiff.find(
    (item) => item.key === `subtopic-max-threshold:${subtopic.id}`,
  )
  const weightDiff = pendingDiff.find(
    (item) => item.key === `subtopic-rollup-weight:${subtopic.id}`,
  )
  const includedDiff = pendingDiff.find(
    (item) => item.key === `subtopic-included:${subtopic.id}`,
  )
  const bucket = proficiencyBucketLabel(
    subtopic.effectiveMastery,
    minThreshold,
    maxThreshold,
  )
  const weightValue = draftWeight ?? String(subtopic.rollupWeight ?? 1)
  const resolvedWeight = resolvedRollupWeight(subtopic, draftWeight)
  const hasCustomWeight = hasCustomRollupWeight(resolvedWeight)
  const showWeightIcon = hasCustomWeight && !shouldShowWeightInput

  const commitWeight = () => {
    if (rangeDisabled) return
    const parsed = Number(weightValue)
    if (!Number.isFinite(parsed) || parsed <= 0) return
    onWeightCommit(parsed)
  }

  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-md py-2 pr-1',
        !subtopicEnabled && 'bg-muted/40 opacity-60',
      )}
      data-subtopic-id={subtopic.id}
    >
      <PlanToggleButton
        topicId={topic.id}
        subtopicId={subtopic.id}
        labelName={subtopic.name}
        disabled={!topic.includedInPlan || !engineEnabled}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <p className="text-foreground min-w-0 truncate text-sm">
            {subtopic.name}
          </p>
          {showWeightIcon && (
            <RollupWeightIcon
              weight={resolvedWeight}
              labelName={subtopic.name}
            />
          )}
        </div>
        <p className="text-muted-foreground text-xs">
          EM {subtopic.effectiveMastery} · {bucket}
          {hasCustomWeight ? ` · wt ${formatRollupWeight(resolvedWeight)}` : ''}
        </p>
        {minThresholdDiff && (
          <div
            data-testid="subtopic-min-diff-marker"
            data-diff-key={minThresholdDiff.key}
            className="mt-1"
          >
            <DiffMarker
              previousValue={minThresholdDiff.previousValue}
              currentValue={minThresholdDiff.currentValue}
            />
          </div>
        )}
        {maxThresholdDiff && (
          <div
            data-testid="subtopic-max-diff-marker"
            data-diff-key={maxThresholdDiff.key}
            className="mt-1"
          >
            <DiffMarker
              previousValue={maxThresholdDiff.previousValue}
              currentValue={maxThresholdDiff.currentValue}
            />
          </div>
        )}
        {weightDiff && (
          <div
            data-testid="subtopic-weight-diff-marker"
            data-diff-key={weightDiff.key}
            className="mt-1"
          >
            <DiffMarker
              previousValue={weightDiff.previousValue}
              currentValue={weightDiff.currentValue}
            />
          </div>
        )}
        {includedDiff && (
          <DiffMarker
            previousValue={includedDiff.previousValue}
            currentValue={includedDiff.currentValue}
            className="mt-1"
          />
        )}
      </div>
      <div className="w-36 shrink-0 sm:w-48">
        <div className="relative pb-5">
          <ProficiencyRangeSlider
            min={minThreshold}
            max={maxThreshold}
            currentMastery={subtopic.effectiveMastery}
            onMinChange={onMinDraftChange}
            onMaxChange={onMaxDraftChange}
            onMinCommit={(value) => {
              if (rangeDisabled) return
              onMinCommit(value)
            }}
            onMaxCommit={(value) => {
              if (rangeDisabled) return
              onMaxCommit(value)
            }}
            hideCurrentMarker={!subtopicEnabled}
            disabled={rangeDisabled}
            aria-label={`${subtopic.name} proficiency goal`}
          />
          <RangeValueLabels min={minThreshold} max={maxThreshold} />
        </div>
      </div>
      {shouldShowWeightInput ? (
        <div className="w-20 shrink-0">
          <label className="sr-only" htmlFor={`weight-${subtopic.id}`}>
            {subtopic.name} rollup weight
          </label>
          <input
            id={`weight-${subtopic.id}`}
            type="number"
            min={0.01}
            step="0.1"
            value={weightValue}
            disabled={rangeDisabled}
            onChange={(event) => onWeightDraftChange(event.target.value)}
            onBlur={commitWeight}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.currentTarget.blur()
              }
              if (event.key === 'Escape') {
                onWeightDraftChange(String(subtopic.rollupWeight ?? 1))
                event.currentTarget.blur()
              }
            }}
            className="bg-background border-border w-full rounded border px-2 py-1 text-xs tabular-nums"
            aria-label={`${subtopic.name} rollup weight`}
          />
        </div>
      ) : null}
    </div>
  )
}

/** Include/exclude toggle for a topic or subtopic row. */
function PlanToggleButton({
  topicId,
  subtopicId,
  labelName,
  disabled: disabledProp,
}: {
  topicId: string
  subtopicId?: string
  labelName: string
  disabled?: boolean
}) {
  const {
    topics,
    toggleTopicInPlan,
    toggleSubtopicInPlan,
    updateProficiencyThresholds,
    engineEnabled,
  } = useRecommendationEngineMock()
  const topic = topics.find((t) => t.id === topicId)
  let included = topic?.includedInPlan ?? false
  if (subtopicId && topic) {
    const st = topic.subtopics.find((s) => s.id === subtopicId)
    included = topic.includedInPlan && st?.isEnabled !== false
  }
  const disabled = disabledProp ?? !engineEnabled

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={included}
      aria-label={
        subtopicId
          ? `Include ${labelName} subtopic in plan`
          : `Include ${labelName} in plan`
      }
      onClick={() => {
        if (!updateProficiencyThresholds) {
          if (subtopicId) toggleSubtopicInPlan(topicId, subtopicId)
          else toggleTopicInPlan(topicId)
          return
        }
        const update: ProficiencyThresholdUpdate = subtopicId
          ? { subtopicUpdates: [{ id: subtopicId, isEnabled: !included }] }
          : { topicUpdates: [{ id: topicId, isEnabled: !included }] }
        // `included` derives from the refetched topics, so a failed toggle
        // simply never moves — the toast is the only signal the coach gets.
        void updateProficiencyThresholds(update).catch((error) =>
          toastError(error, 'Failed to update plan inclusion'),
        )
      }}
      disabled={disabled}
      className={cn(
        'relative h-5 w-9 shrink-0 rounded-full transition-colors',
        included ? 'bg-primary' : 'bg-muted-foreground/30',
        disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      <span
        className={cn(
          'bg-background absolute top-0.5 h-4 w-4 rounded-full shadow transition-transform',
          included ? 'translate-x-4' : 'translate-x-0.5',
        )}
      />
    </button>
  )
}
